import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, User, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence, easeLuxury } from "./motion";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/booking", label: "Booking" },
  { to: "/room", label: "Room" },
  { to: "/gallery", label: "Gallery" },
  { to: "/amenities", label: "Amenities" },
  { to: "/rules", label: "Rules" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: easeLuxury }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "glass-strong h-16 border-b border-white/[0.05]"
          : "glass-strong h-20"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-full flex items-center justify-between">
        {/* ROOM XIII Logo */}
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <motion.img
            src="/logo (2).png"
            alt="ROOM XIII"
            style={{ height: scrolled ? 38 : 44, width: "auto", transition: "height 0.4s ease" }}
            whileHover={{ filter: "drop-shadow(0 0 10px rgba(201,162,75,0.55))" }}
            transition={{ duration: 0.35 }}
          />
          <div
            className="font-cormorant tracking-[0.28em] hidden sm:block"
            style={{ fontSize: "1.2rem", fontWeight: 400 }}
          >
            <span className="text-gold">ROOM</span>
            <span className="text-crimson-bright"> XIII</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {navLinks.map((l, i) => (
            <motion.div
              key={l.to}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.05, ease: easeLuxury }}
            >
              <NavLink
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-xs tracking-[0.2em] uppercase font-light transition-colors link-underline ${
                    isActive ? "text-gold" : "text-white/60 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </motion.div>
          ))}
        </nav>

        {/* Desktop auth + CTA */}
        <div className="hidden lg:flex items-center gap-5">
          {user && (
            <>
              <Link
                to={user.role === "admin" ? "/admin" : "/dashboard"}
                data-testid="dashboard-link"
                className="text-xs tracking-[0.2em] uppercase text-white/70 hover:text-gold flex items-center gap-2 transition-colors"
              >
                <User size={13} />
                {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-white/40 hover:text-gold transition-colors"
                aria-label="logout"
              >
                <LogOut size={15} />
              </button>
            </>
          )}
          <Link
            to="/booking"
            data-testid="navbar-book-now"
            className="btn-gold magnetic"
          >
            Book Now
          </Link>
        </div>

        {/* Mobile menu toggle */}
        <button
          className="lg:hidden text-white/80 hover:text-gold transition-colors"
          onClick={() => setOpen(!open)}
          aria-label="menu"
          data-testid="mobile-menu-toggle"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={open ? "close" : "open"}
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </motion.div>
          </AnimatePresence>
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="lg:hidden glass-strong border-t border-white/[0.05] overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.48, ease: easeLuxury }}
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {navLinks.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.38, delay: i * 0.055, ease: easeLuxury }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="text-sm tracking-[0.22em] uppercase text-white/70 hover:text-gold transition-colors"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <div className="pt-5 border-t border-white/[0.06] flex flex-col gap-4">
                {user && (
                  <>
                    <Link
                      to={user.role === "admin" ? "/admin" : "/dashboard"}
                      onClick={() => setOpen(false)}
                      className="text-sm tracking-[0.22em] uppercase text-gold"
                      data-testid="mobile-dashboard-link"
                    >
                      My Account
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setOpen(false); }}
                      className="text-sm tracking-[0.22em] uppercase text-white/50 text-left"
                      data-testid="mobile-logout-button"
                    >
                      Sign Out
                    </button>
                  </>
                )}
                <Link
                  to="/booking"
                  onClick={() => setOpen(false)}
                  className="btn-gold text-center"
                  data-testid="mobile-book-now"
                >
                  Book Now
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
